import React from "react";
import { FixedSizeList } from "react-window";
import "./index.css";
import { useEffect, useState } from "react";
import AutoSizer from "react-virtualized-auto-sizer";
import { MadeByShort } from "./components/MadeBy";
import { PartsImage } from "./components/PartsImage";
import { BASE_URL } from "./utils/constants";
import { ShopTheme } from "./types/types";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import translationEN from "./locales/en/translations.json";
import translationRO from "./locales/ro/translations.json";
import { parseModelIdFromLocationHash } from "./utils/locationHashUtils";
import { readLocationHashStorage } from "./services/local-storage-service";

const CARD_WIDTH = 300;
const ROOT_HEIGHT = 570;
const IMAGE_HEIGHT_SIZE = 500;

type CompatibilityWidgetProps = {
  permanentDomain: string;
  sku: string;
  countryCode?: string;
};

const resources: Record<string, { translation: Record<string, string> }> = {
  en: {
    translation: translationEN,
  },
  ro: {
    translation: translationRO,
  },
};

i18n.use(initReactI18next).init({
  resources,
  lng: "ro", // default RO
  fallbackLng: "en",
  interpolation: {
    escapeValue: false,
  },
});

export default function CompatibilityWidget({ permanentDomain, sku, countryCode }: CompatibilityWidgetProps) {
  const [dataInChunks, setDataInChunks] = useState<any[]>([]);
  const [ktmPartsFinderPath, setKtmPartsFinderPath] = useState();
  const [husqvarnaPartsFinderPath, setHusqvarnaPartsFinderPath] = useState();
  const [gasgasPartsFinderPath, setGasgasPartsFinderPath] = useState();
  const [modalData, setModalData] = useState();
  const [theme, setTheme] = useState<ShopTheme>();
  const { t } = useTranslation();
  const [isCompatibleWithModelId, setIsCompatibleWithModelId] = useState(false);
  const [yourModelName, setYourModelName] = useState<string | undefined>();

  // Update language when countryCode is available
  useEffect(() => {
    if (countryCode) {
      const language = countryCode.toLowerCase();

      if (resources[language]) {
        i18n.changeLanguage(language);
      } else {
        // Fallback to default language if country code is not supported
        i18n.changeLanguage(i18n.options.fallbackLng as string);
      }
    } else {
      // Fallback to default language if country code is not supported
      i18n.changeLanguage(i18n.options.fallbackLng as string);
    }
  }, [countryCode]);

  const API_URL = `${BASE_URL}/api/compatibility?permanentDomain=${permanentDomain}&sku=${encodeURIComponent(sku)}`;

  useEffect(() => {
    const locationHash = readLocationHashStorage();
    const modelId = locationHash ? parseModelIdFromLocationHash(locationHash) : undefined;
    let isCompatibleWithModelId = false;
    let yourModelName: string | undefined = undefined;

    fetch(API_URL)
      .then((res) => res.json())
      .then((res) => {
        const data = res.data;

        console.log("data", data);

        const dataInChunks: any[] = [];
        let chunk: any[] = [];
        data.forEach((d: any, index: number) => {
          chunk.push(d);
          if ((index + 1) % 7 === 0) {
            dataInChunks.push(chunk);
            chunk = [];
          }

          if (!isCompatibleWithModelId) {
            d?.diagrams?.forEach((diagram: any) => {
              if (diagram.modelId === modelId) {
                isCompatibleWithModelId = true;
                yourModelName = d.modelName;
              }
            });
          }
        });

        console.log("isCompatibleWithModelId", isCompatibleWithModelId);
        console.log("yourModelName", yourModelName);

        setTheme(res.theme);
        setKtmPartsFinderPath(res.ktmPartsFinderPath);
        setHusqvarnaPartsFinderPath(res.husqvarnaPartsFinderPath);
        setGasgasPartsFinderPath(res.gasgasPartsFinderPath);
        setDataInChunks(dataInChunks);
        setIsCompatibleWithModelId(isCompatibleWithModelId);
        setYourModelName(yourModelName);
      })
      .catch((err) => {
        console.error("Error fetching compatibility data", err);
      });
  }, []);

  const onClickOpenPartsFinder = (brand: string, year: string, ridingTypeName: string, modelId: string, diagramId: string) => {
    if (!brand || !year || !ridingTypeName || !modelId || !diagramId) {
      return;
    }

    const path = [brand, year, ridingTypeName, modelId, diagramId].map((t) => t.replaceAll(" ", "+")).join("-");

    const pathForBrand =
      brand.toUpperCase() === "KTM"
        ? ktmPartsFinderPath
        : brand.toUpperCase() === "HUSQVARNA"
        ? husqvarnaPartsFinderPath
        : brand.toUpperCase() === "GASGAS"
        ? gasgasPartsFinderPath
        : undefined;

    const finalPath = `${pathForBrand}?location=` + path;

    const newPage = window.location.protocol + "//" + window.location.hostname + finalPath;
    window.open(newPage, "_self");
  };

  const Row = ({ data, index }: { data: any; index: number }) => {
    const t = data;

    return (
      <div key={index} className="w-full">
        <div key={t.modelName} className="w-full">
          <div className="flex mt-1 w-ful w-full">
            <div className="ml-2 flex w-full" style={{ height: 50 }}>
              <button
                className="h-full bg-transparent text-black font-semibold px-4 rounded hover:bg-gray-800 hover:text-white"
                style={{ width: CARD_WIDTH * 0.95 }}
                onClick={() => {
                  console.log(t);
                  setModalData(
                    t.diagrams.map((d: any) => {
                      return {
                        key: d.diagramName,
                        partPositionNo: d.partPositionNo,
                        diagramId: d.diagramId,
                        title: d.diagramName,
                        brand: d.brand,
                        fileName: d.fileName,
                        year: d.year,
                        ridingTypeName: d.ridingTypeName,
                        modelId: d.modelId,
                        modelName: t.modelName,
                        points: t.points,
                      };
                    })
                  );
                }}
              >
                <div className="flex">
                  <div className="grow text-left w-full" style={{ fontSize: 14 }}>
                    {t.modelName.toUpperCase()}
                  </div>
                  <div className="flex-none top-3 right-3">
                    <ExternalIcon />
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const Card = ({ data, index, style }: { data: any; index: number; style: any }) => {
    const rows = data[index];

    return (
      <div style={style} key={index} className="flex justify-around">
        <div>
          {rows.map((row: any, index: number) => (
            <Row key={index} data={row} index={index} />
          ))}
        </div>
      </div>
    );
  };

  if (!dataInChunks || dataInChunks.length === 0 || !theme || !ktmPartsFinderPath) {
    return null;
  }

  return (
    <div
      className="flex flex-col w-full justify-center items-center"
      style={{
        height: ROOT_HEIGHT,
        backgroundColor: theme.compatibilityWidget.bgColor,
        padding: theme.compatibilityWidget.padding,
      }}
    >
      <div className="w-full h-full flex flex-col">
        <div className="flex items-center">
          <img
            className="flex-none"
            src="https://partsfinder.clpstudio.ro/genuinespareparts.webp"
            style={{ height: 80, width: "auto", borderRadius: 20 }}
            alt=""
          />

          <div className="ms-2 mb-2 ">
            <h2 className="lg:text-2xl text-base font-semibold text-gray-900">{t("compatibility_card_title")}</h2>
            <span className="lg:text-sm text-xs">{t("compatibility_card_description")}</span>
          </div>
        </div>

        {isCompatibleWithModelId && yourModelName && (
          <div className="flex items-center mt-3">
            <span className="text-green-600 font-bold">{t("compatibility_card_description_compatible_with_model") + " " + yourModelName} </span>
          </div>
        )}

        <div className="h-full w-full mt-1">
          <AutoSizer>
            {({ height, width }) => (
              // @ts-ignore
              <FixedSizeList
                height={height}
                itemCount={dataInChunks.length || 0}
                itemSize={CARD_WIDTH}
                width={width}
                itemData={dataInChunks}
                direction="horizontal"
              >
                {Card}
              </FixedSizeList>
            )}
          </AutoSizer>
        </div>
      </div>

      <Modal
        isOpen={modalData !== undefined}
        data={modalData}
        onClose={() => {
          setModalData(undefined);
        }}
        onClickOpenPartsFinder={onClickOpenPartsFinder}
      />

      <div className="flex w-full justify-end pe-5">
        <MadeByShort />
      </div>
    </div>
  );
}

const Modal = ({
  isOpen,
  data,
  onClose,
  onClickOpenPartsFinder,
}: {
  isOpen: boolean;
  data: any;
  onClose: () => void;
  onClickOpenPartsFinder: (brand: string, year: string, ridingTypeName: string, modelId: string, diagramId: string) => void;
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <div
      className="p-2 fixed inset-0 w-full flex items-center justify-centerbg-black bg-opacity-50 bg-black dark:bg-black400 h-full justify-center items-center"
      style={{
        zIndex: 9990,
      }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-lg p-5 w-full relative overflow-y-auto"
        style={{ width: "95%", maxHeight: "90svh" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-full h-full pt-1 justify-center items-center">
          {data.map((d: any) => (
            <div>
              <ExpandedCard
                key={d.diagramId}
                partPositionNo={d.partPositionNo}
                // @ts-ignore
                diagramId={d.diagramId}
                title={d.title}
                brand={d.brand}
                fileName={d.fileName}
                modelName={d.modelName}
                onClickOpenPartsFinder={() => {
                  onClickOpenPartsFinder(d.brand, d.year, d.ridingTypeName, d.modelId, d.diagramId);
                }}
                circlePoints={d.points}
              />
              <hr className="mt-2 w-full bg-gray" style={{ height: 2 }} />
            </div>
          ))}
        </div>

        <div className="absolute top-3 right-3 text-black hover:text-black" onClick={onClose}>
          {/* @ts-ignore */}
          <button size="larger" variant="plain" onClick={onClose}>
            <svg className="w-[20px] h-[20px] fill-[#8e8e8e]" viewBox="0 0 384 512" xmlns="http://www.w3.org/2000/svg">
              <path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"></path>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

const ExpandedCard = ({
  title,
  modelName,
  fileName,
  partPositionNo,
  onClickOpenPartsFinder,
  circlePoints,
}: {
  title: string;
  modelName: string;
  fileName: string;
  partPositionNo: string;
  onClickOpenPartsFinder: () => void;
  circlePoints: string[];
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-between p-4 bg-white dark:bg-white h-full">
      <div className="flex flex-col w-full h-full text-center items-center justify-center">
        <div>
          {modelName && (modelName + "").trim().length > 0 && (
            <span className="text-black">
              <strong style={{ fontSize: 16, color: "#FF6600" }}>{modelName}</strong>
            </span>
          )}
        </div>
        <strong className="text-lg font-semibold text-black mt-1">{title}</strong>
        <div>
          {partPositionNo && (partPositionNo + "").trim().length > 0 && (
            <span className="text-black">
              {t("compatibility_modal_subtitle_1")}
              <strong style={{ fontSize: 16, color: "#FF6600" }}>{" " + partPositionNo + " "}</strong>
              {t("compatibility_modal_subtitle_2")}
            </span>
          )}

          <PartsImage
            className="mt-2 flex grow"
            src={`https://partsfinder.clpstudio.ro/${fileName}`}
            circleCssPoints={circlePoints}
            // @ts-ignore
            style={{ width: "auto", height: IMAGE_HEIGHT_SIZE }}
          />

          <div className="w-full flex justify-end mt-2">
            <button
              className="bg-black hover:bg-gray-800 text-white font-semibold hover:text-white py-2 px-4 border border-black hover:border-transparent rounded"
              style={{
                border: "1px solid lightgray",
              }}
              onClick={() => {
                onClickOpenPartsFinder();
              }}
            >
              <div className="flex">
                <div>{t("compatibility_modal_see_all_parts")}</div>
                <div className="ms-1">
                  <ExternalIcon />
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ExternalIcon = () => (
  <svg className="w-[10px] h-[10px] fill-[#8e8e8e]" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
    <path d="M352 0c-12.9 0-24.6 7.8-29.6 19.8s-2.2 25.7 6.9 34.9L370.7 96 201.4 265.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L416 141.3l41.4 41.4c9.2 9.2 22.9 11.9 34.9 6.9s19.8-16.6 19.8-29.6V32c0-17.7-14.3-32-32-32H352zM80 32C35.8 32 0 67.8 0 112V432c0 44.2 35.8 80 80 80H400c44.2 0 80-35.8 80-80V320c0-17.7-14.3-32-32-32s-32 14.3-32 32V432c0 8.8-7.2 16-16 16H80c-8.8 0-16-7.2-16-16V112c0-8.8 7.2-16 16-16H192c17.7 0 32-14.3 32-32s-14.3-32-32-32H80z"></path>
  </svg>
);
